import { shipToteThunk } from '@redux/features/order.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import integrationsService from 'services/api/integrations.service';
import React, { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';

type ToteReadiness = {
    ready?: boolean;
    issues?: string[];
    products?: { productId?: number; itemName?: string; ready?: boolean; issues?: string[] }[];
    pharmacy?: { name?: string; ready?: boolean; issues?: string[] };
};

type IProps = {
    show: boolean;
    handleClose: () => void;
    toteId: number | null;
    onShipComplete?: () => void;
};

const ShipToteModal: FC<IProps> = ({ show, handleClose, toteId, onShipComplete }) => {
    const { shipToteLoading } = useAppSelector((state: RootState) => state.orderState);
    const dispatch = useAppDispatch();
    const [readinessWarning, setReadinessWarning] = useState<string | null>(null);
    const [toteReadiness, setToteReadiness] = useState<ToteReadiness | null>(null);
    const [readinessLoading, setReadinessLoading] = useState(false);

    useEffect(() => {
        if (!show) {
            setReadinessWarning(null);
            setToteReadiness(null);
            return;
        }
        setReadinessLoading(true);
        const requests: Promise<unknown>[] = [integrationsService.getEpcisDataHealth()];
        if (toteId) {
            requests.push(integrationsService.getToteEpcisReadiness(toteId));
        }
        Promise.all(requests)
            .then((results: any[]) => {
                const healthRes = results[0];
                const h = healthRes.data?.dataHealth;
                const issues: string[] = [];
                if ((h?.pharmacyGlnWithoutSgln ?? 0) > 0) {
                    issues.push(`${h.pharmacyGlnWithoutSgln} pharmacy account(s) missing SGLN`);
                }
                if ((h?.pharmaciesMissingPartyAddress ?? 0) > 0) {
                    issues.push(`${h.pharmaciesMissingPartyAddress} pharmacy account(s) missing ship-to address`);
                }
                setReadinessWarning(issues.length ? issues.join('; ') : null);

                if (results[1]) {
                    setToteReadiness(results[1].data?.readiness ?? null);
                } else {
                    setToteReadiness(null);
                }
            })
            .catch(() => {
                setReadinessWarning(null);
                setToteReadiness(null);
            })
            .finally(() => setReadinessLoading(false));
    }, [show, toteId]);

    const validationSchema = Yup.object({
        customerNote: Yup.string().optional(),
    });

    const formik = useFormik({
        initialValues: {
            customerNote: '',
        },
        validationSchema,
        onSubmit: (values) => {
            if (toteId && !shipToteLoading) {
                dispatch(shipToteThunk({ toteId, customerNote: values.customerNote || undefined })).then((res: any) => {
                    if (res.meta.requestStatus === 'fulfilled') {
                        formik.resetForm();
                        handleClose();
                        if (onShipComplete) onShipComplete();
                    }
                });
            }
        },
    });

    useEffect(() => {
        if (!show) formik.resetForm();
    }, [show]);

    const toteBlocked = toteReadiness?.ready === false;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-6 shadow-xl rounded-lg border border-gray-200 overflow-scroll md:w-[550px] md:max-h-[85vh]"
            backdropClassName="backdrop-overlay fixed inset-0 z-[1051] bg-black/70 backdrop-blur-sm"
        >
            <Modal.Header className="border-b border-gray-200 pb-4 mb-4 flex justify-between items-center">
                <Modal.Title className="text-xl font-semibold text-gray-800">Ship Tote</Modal.Title>
            </Modal.Header>

            <Modal.Body className="my-4">
                <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                    Shipping generates outbound EPCIS (SFTP/AS2). Each RX product needs{' '}
                    <strong>companyPrefix</strong>, <strong>itemReference</strong>, and <strong>GTIN-14</strong>; the
                    pharmacy needs GLN, SGLN, and ship-to address.
                </div>
                {readinessLoading ? (
                    <p className="mb-4 text-sm text-slate-500">Checking GS1 readiness for this tote…</p>
                ) : null}
                {readinessWarning ? (
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                        Platform data health: {readinessWarning}.
                    </div>
                ) : null}
                {toteReadiness ? (
                    <div
                        className={`mb-4 rounded-md border px-3 py-2 text-sm ${
                            toteBlocked
                                ? 'border-red-200 bg-red-50 text-red-900'
                                : 'border-green-200 bg-green-50 text-green-900'
                        }`}
                    >
                        <p className="font-semibold mb-2">
                            Pre-ship checklist: {toteBlocked ? 'issues found' : 'ready for outbound EPCIS'}
                        </p>
                        {toteReadiness.pharmacy ? (
                            <div className="mb-2">
                                <span className="font-medium">Pharmacy ({toteReadiness.pharmacy.name || 'order account'}): </span>
                                {toteReadiness.pharmacy.ready ? 'OK' : toteReadiness.pharmacy.issues?.join(', ')}
                            </div>
                        ) : null}
                        {(toteReadiness.products ?? []).map((product) => (
                            <div key={product.productId} className="mb-1">
                                <span className="font-medium">{product.itemName}: </span>
                                {product.ready ? 'OK' : product.issues?.join(', ')}
                            </div>
                        ))}
                        {toteBlocked && toteReadiness.issues?.length ? (
                            <p className="mt-2 text-xs">{toteReadiness.issues.join('; ')}</p>
                        ) : null}
                    </div>
                ) : null}
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Customer Note (Optional)</label>
                        <textarea
                            id="customerNote"
                            name="customerNote"
                            value={formik.values.customerNote}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={shipToteLoading}
                            rows={4}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none"
                            placeholder="Enter customer note (optional)..."
                            autoFocus
                        />
                        {formik.touched.customerNote && formik.errors.customerNote && (
                            <p className="mt-2 text-sm text-red-600 font-medium">
                                {String(formik.errors.customerNote)}
                            </p>
                        )}
                    </div>
                </form>
            </Modal.Body>

            <Modal.Footer className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={shipToteLoading}
                />
                <ButtonBase
                    innerText={shipToteLoading ? 'Shipping...' : 'Ship'}
                    className="bg-blue-600 hover:bg-blue-700 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={shipToteLoading || !toteId || toteBlocked}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default ShipToteModal;
